import { RadioGroup, Tooltip } from '@pique/component';
import { useLiveData, useService } from '@toeverything/infra';
import { useCallback } from 'react';

import type { SidebarTab } from '../../entities/sidebar-tab';
import { ViewService } from '../../services/view';
import { ViewSidebarTabIconTarget } from '../view-islands';
import * as styles from './sidebar-header-switcher.css';

const TAB_LABELS: Record<string, string> = {
  chat: 'AI Chat',
  'followup-email': 'Follow-up Email',
  outline: 'Outline',
  frame: 'Frame',
};

// provide a switcher for active extensions
// will be used in global top header (MacOS) or sidebar (Windows)
export const SidebarHeaderSwitcher = () => {
  const view = useService(ViewService).view;
  const tabs = useLiveData(view.sidebarTabs$);
  const activeTab = useLiveData(view.activeSidebarTab$);

  const tabItems = tabs.map((tab: SidebarTab) => ({
    value: tab.id,
    label: (
      <ViewSidebarTabIconTarget
        className={styles.iconContainer}
        viewId={view.id}
        tabId={tab.id}
      />
    ),
    style: { padding: 0, fontSize: 16, width: 24 },
    attrs: {
      'aria-label': TAB_LABELS[tab.id] || tab.id,
    },
    customRender: () => (
      <Tooltip content={TAB_LABELS[tab.id] || tab.id} side="bottom">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
          <ViewSidebarTabIconTarget
            className={styles.iconContainer}
            viewId={view.id}
            tabId={tab.id}
          />
        </div>
      </Tooltip>
    ),
  }));

  const handleActiveTabChange = useCallback(
    (tabId: string) => {
      view.activeSidebarTab(tabId);
    },
    [view]
  );

  return tabItems.length ? (
    <div
      data-testid="sidebar-header-switcher"
      style={{ display: 'inline-flex' }}
      data-tauri-drag-region="false"
    >
      <RadioGroup
        iconMode
        borderRadius={20}
        itemHeight={24}
        padding={4}
        gap={4}
        items={tabItems}
        value={activeTab?.id ?? tabs[0]?.id ?? ''}
        onChange={handleActiveTabChange}
      />
    </div>
  ) : null;
};
